import React, { FC, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import HandySettingsBlock from '~Scenes/Handy/Components/HandySettingsBlock';
import { CLIAM_DEVICE, GET_DEVICES } from '~Scenes/Settings-v2/Components/Device';
import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import * as actions from '~actions';
import { Box } from '~Styles';
import Loader from '~ComponentsImproved/Loader';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useStorage } from '~Components/Storage';
import {
  ClaimDevice,
  ClaimDeviceVariables,
  getDevices,
} from '~Scenes/Settings-v2/Components/__queries__';

const SelectDevice: FC = () => {
  const { i18n } = useI18n();

  const [storage, updateStorage] = useStorage();
  const dispatch = useDispatch();
  const logger = useLogger('handy-settings');

  const [loading, setLoading] = useState(false);

  const venueId = useSelector((state) => state.venueId);
  const companyId = useSelector((state) => state.company);

  const devicesQuery = useQuery<getDevices>(GET_DEVICES, {
    variables: { venueId, companyId },
    skip: !venueId || !companyId,
    fetchPolicy: 'cache-and-network',
  });

  const devices = devicesQuery.data?.devices ?? [];

  const [claimDevice] = useMutation<ClaimDevice, ClaimDeviceVariables>(CLIAM_DEVICE, {
    refetchQueries: ['getDevices'],
    awaitRefetchQueries: true,
  });

  const handleUpdate = async (id: string) => {
    if (!devicesQuery.data) {
      return;
    }

    setLoading(true);

    const corporateId = devicesQuery.data.venue.organizationId.replace(/\D/g, '');

    const device = devicesQuery.data.devices.find((device) => device.id === id)!;
    let name = device.name;

    dispatch({
      type: actions.MESSAGE_RECEIPT_COUNTER,
      receiptCounter: 0,
    });

    if (!device.claimed) {
      try {
        const { data: result, errors } = await claimDevice({
          variables: { deviceId: id, lastSeen: new Date() },
        });

        if (result?.updatePOSDevice) {
          id = result.updatePOSDevice.id;
          name = result.updatePOSDevice.name;
        } else {
          setLoading(false);
          console.error(errors);
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
      }
    }
    setLoading(false);

    updateStorage({
      taxComplianceDeviceId: id,
      taxComplianceDeviceName: name,
      taxComplianceCorporateId: corporateId,
    });

    logger.info('Updating Settings (change device)', {
      fields: { name, id, corporateId },
    });
  };

  if (devicesQuery.loading) {
    return (
      <Box
        style={{ height: '50vh' }}
        display="flex"
        alignItems="center"
        flexDirection="column"
        gap="small"
        justifyContent="center">
        <Loader />
        <Paragraph level={4} color="var(--colorWhite)">
          {i18n('Settings.Device.Loading')}
        </Paragraph>
      </Box>
    );
  }

  return (
    <HandySettingsBlock
      items={devices.map((device) => ({
        id: device.id,
        title: `${device.name} ${device.claimed ? '(Claimed)' : ''} ${
          storage.taxComplianceDeviceId === device.id ? i18n('Handy.Active') : ''
        }`,
        action: {
          onClick() {
            handleUpdate(device.id);
          },
        },
      }))}
    />
  );
};

export default SelectDevice;
